<script>
import { ref, onMounted, onBeforeUnmount, computed, watch } from 'vue'
import { useRouter } from 'vue-router'
import { useRestoGLobalStore } from '@/stores/restoGlobalStore'
import { useCartGLobalStore } from '@/stores/cartGlobalStore'
import { useThemeStore } from '@/stores/themeStore'
import { createMolliePaymentFn } from '@/services/api/restApiCalls'

const MOLLIE_SCRIPT_ID = 'mollie-js-v1'

const FIELD_MOUNTS = [
  {
    type: 'cardNumber',
    selector: '#mollie-card-number',
    placeholder: '1234 1234 1234 1234',
    fallbackError: 'Your card number is incomplete.'
  },
  {
    type: 'expiryDate',
    selector: '#mollie-expiry-date',
    placeholder: 'MM / YY',
    fallbackError: "Your card's expiration date is incomplete."
  },
  {
    type: 'verificationCode',
    selector: '#mollie-verification-code',
    placeholder: 'CVC',
    fallbackError: "Your card's security code is incomplete."
  },
  {
    type: 'cardHolder',
    selector: '#mollie-card-holder',
    placeholder: 'Name on card',
    fallbackError: 'Please enter the name on the card.'
  }
]

const EMPTY_FIELD_STATE = () =>
  FIELD_MOUNTS.reduce((acc, field) => {
    acc[field.type] = {
      error: '',
      valid: false,
      dirty: false,
      focused: false
    }
    return acc
  }, {})

function loadMollieScript() {
  return new Promise((resolve, reject) => {
    if (window.Mollie) {
      resolve(window.Mollie)
      return
    }
    const existing = document.getElementById(MOLLIE_SCRIPT_ID)
    if (existing) {
      existing.addEventListener('load', () => resolve(window.Mollie))
      existing.addEventListener('error', reject)
      return
    }
    const script = document.createElement('script')
    script.id = MOLLIE_SCRIPT_ID
    script.src = 'https://js.mollie.com/v1/mollie.js'
    script.async = true
    script.onload = () => resolve(window.Mollie)
    script.onerror = reject
    document.head.appendChild(script)
  })
}

function isDarkMode() {
  return document.documentElement.classList.contains('dark')
}

function getMollieInputStyles() {
  const dark = isDarkMode()
  return {
    base: {
      color: dark ? '#e5e7eb' : '#32325d',
      backgroundColor: 'transparent',
      fontSize: '16px',
      fontWeight: '400',
      '::placeholder': {
        color: dark ? '#9ca3af' : '#9ca3af'
      }
    },
    valid: {
      color: dark ? '#e5e7eb' : '#32325d'
    },
    invalid: {
      color: dark ? '#f87171' : '#ef4444'
    }
  }
}

export default {
  props: {
    profileId: { type: String, required: true },
    testmode: { type: Boolean, default: false },
    beforePlaceOrder: { type: Function, default: null }
  },

  emits: ['pay-button-state'],

  setup(props, { expose, emit }) {
    const router = useRouter()
    const restoStore = useRestoGLobalStore()
    const cartStore = useCartGLobalStore()
    const themeStore = useThemeStore()

    const mollie = ref(null)
    const cardErrorMsg = ref('')
    const payButtonDisabled = ref(true)
    const isProcessing = ref(false)
    const isReady = ref(false)
    const isUnmounted = ref(false)
    const isDark = computed(() => themeStore.theme === 'dark')
    const fieldState = ref(EMPTY_FIELD_STATE())
    const mountedComponents = []

    const formattedAmount = computed(() => {
      const total = cartStore.CART_DATA?.order_total || 0
      return `£${Number(total).toFixed(2)}`
    })

    const showPlaceholder = (fieldType) => {
      const field = fieldState.value[fieldType]
      return !field?.dirty && !field?.focused
    }

    const syncPayButtonState = () => {
      const hasFieldError = FIELD_MOUNTS.some(({ type }) => fieldState.value[type]?.error)
      const allValid = FIELD_MOUNTS.every(({ type }) => fieldState.value[type]?.valid)
      payButtonDisabled.value = !isReady.value || hasFieldError || !allValid
    }

    const updateFieldState = (event = {}, fieldType) => {
      const fieldConfig = FIELD_MOUNTS.find((field) => field.type === fieldType)
      const nextState = { ...fieldState.value[fieldType] }
      nextState.dirty = !!event.dirty || nextState.dirty
      nextState.touched = !!event.touched || nextState.touched

      if (event.error && event.touched) {
        nextState.error =
          (typeof event.error === 'string' ? event.error : event.error?.message) ||
          fieldConfig?.fallbackError ||
          ''
        nextState.valid = false
      } else {
        nextState.error = ''
        if (typeof event.valid === 'boolean') {
          nextState.valid = event.valid
        } else if (event.complete === true) {
          nextState.valid = true
        } else if (nextState.dirty && !event.error) {
          nextState.valid = true
        }
      }

      fieldState.value = {
        ...fieldState.value,
        [fieldType]: nextState
      }
      syncPayButtonState()
    }

    const applyTokenError = (message) => {
      const lowerMessage = message.toLowerCase()
      let target = ''
      if (lowerMessage.includes('expir')) {
        target = 'expiryDate'
      } else if (
        lowerMessage.includes('security') ||
        lowerMessage.includes('cvc') ||
        lowerMessage.includes('verif')
      ) {
        target = 'verificationCode'
      } else if (lowerMessage.includes('name') || lowerMessage.includes('holder')) {
        target = 'cardHolder'
      } else if (lowerMessage.includes('card') || lowerMessage.includes('number')) {
        target = 'cardNumber'
      }

      if (!target) {
        cardErrorMsg.value = message
        syncPayButtonState()
        return
      }

      fieldState.value = {
        ...fieldState.value,
        [target]: { ...fieldState.value[target], error: message, valid: false }
      }
      syncPayButtonState()
    }

    const unmountCardForm = () => {
      mountedComponents.forEach((component) => {
        if (component?.unmount) {
          component.unmount()
        }
      })
      mountedComponents.length = 0
    }

    const mountCardForm = () => {
      if (!mollie.value || isUnmounted.value) return
      unmountCardForm()
      fieldState.value = EMPTY_FIELD_STATE()
      cardErrorMsg.value = ''

      FIELD_MOUNTS.forEach(({ type, selector, placeholder }) => {
        if (!document.querySelector(selector)) return
        const component = mollie.value.createComponent(type, {
          styles: getMollieInputStyles(),
          placeholder
        })
        component.mount(selector)
        component.addEventListener('change', (event) => {
          updateFieldState(event, type)
        })
        component.addEventListener('focus', () => {
          fieldState.value = {
            ...fieldState.value,
            [type]: { ...fieldState.value[type], focused: true }
          }
        })
        component.addEventListener('blur', () => {
          fieldState.value = {
            ...fieldState.value,
            [type]: { ...fieldState.value[type], focused: false }
          }
        })
        mountedComponents.push(component)
      })

      isReady.value = true
      syncPayButtonState()
    }

    const handleMolliePayment = async () => {
      if (isUnmounted.value || isProcessing.value || !isReady.value) return

      isProcessing.value = true
      cardErrorMsg.value = ''

      try {
        if (props.beforePlaceOrder) {
          const ready = await props.beforePlaceOrder()
          if (!ready) {
            isProcessing.value = false
            return
          }
        }

        const { token, error } = await mollie.value.createToken()
        if (error) {
          applyTokenError(error.message || 'Card validation failed')
          isProcessing.value = false
          return
        }

        const response = await createMolliePaymentFn(restoStore?.RESTO_ID, token)
        if (!response || response?.status >= 400 || response?.error_code) {
          cardErrorMsg.value = response?.status_msg || 'Payment failed. Please try again.'
          isProcessing.value = false
          return
        }

        if (response.order_status === 'AUTHORIZED' && response.order_id) {
          isProcessing.value = false
          router.push({
            name: 'order_status',
            params: { order_id: `${response.order_id}` }
          })
          cartStore.CART_DATA = ''
          return
        }

        if (response.checkout_url) {
          sessionStorage.setItem('mollie_order_id', `${response.order_id}`)
          window.location.href = response.checkout_url
          return
        }

        cardErrorMsg.value = 'Unable to start payment. Please try again.'
        isProcessing.value = false
      } catch (err) {
        cardErrorMsg.value = err?.message || 'Payment failed. Please try again.'
        isProcessing.value = false
      }
    }

    onMounted(async () => {
      isUnmounted.value = false
      try {
        await loadMollieScript()
        mollie.value = window.Mollie(props.profileId, {
          locale: 'en_GB',
          testmode: props.testmode
        })
        mountCardForm()
      } catch (err) {
        cardErrorMsg.value = 'Unable to load Mollie payment form.'
        payButtonDisabled.value = true
      }
    })

    onBeforeUnmount(() => {
      isUnmounted.value = true
      unmountCardForm()
    })

    watch(
      () => themeStore.theme,
      () => {
        if (isReady.value && !isProcessing.value) {
          mountCardForm()
        }
      }
    )

    watch(
      payButtonDisabled,
      (disabled) => {
        emit('pay-button-state', disabled !== false)
      },
      { immediate: true }
    )

    expose({
      handleMolliePayment,
      handleStripePayment: handleMolliePayment,
      payButtonDisabled
    })

    return {
      cardErrorMsg,
      fieldState,
      payButtonDisabled,
      isProcessing,
      isDark,
      formattedAmount,
      showPlaceholder,
      handleMolliePayment
    }
  }
}
</script>

<template>
  <div class="mollie-payment-element-wrapper" :class="isDark ? 'mollie-theme-dark' : 'mollie-theme-light'">
    <div class="mollie-card-panel">
      <div class="mollie-field-group">
        <label for="mollie-card-number" class="mollie-field-label">Card number</label>
        <div class="mollie-field-wrap" :class="{ 'is-invalid': fieldState.cardNumber.error }">
          <div id="mollie-card-number" class="mollie-field"></div>
          <span v-show="showPlaceholder('cardNumber')" class="mollie-placeholder">1234 1234 1234 1234</span>
        </div>
        <p v-if="fieldState.cardNumber.error" class="mollie-field-error">{{ fieldState.cardNumber.error }}</p>
      </div>
      <div class="mollie-field-row">
        <div class="mollie-field-group">
          <label for="mollie-expiry-date" class="mollie-field-label">Expiration date</label>
          <div class="mollie-field-wrap" :class="{ 'is-invalid': fieldState.expiryDate.error }">
            <div id="mollie-expiry-date" class="mollie-field"></div>
            <span v-show="showPlaceholder('expiryDate')" class="mollie-placeholder">MM / YY</span>
          </div>
          <p v-if="fieldState.expiryDate.error" class="mollie-field-error">{{ fieldState.expiryDate.error }}</p>
        </div>
        <div class="mollie-field-group">
          <label for="mollie-verification-code" class="mollie-field-label">Security code</label>
          <div class="mollie-field-wrap" :class="{ 'is-invalid': fieldState.verificationCode.error }">
            <div id="mollie-verification-code" class="mollie-field"></div>
            <span v-show="showPlaceholder('verificationCode')" class="mollie-placeholder">CVC</span>
          </div>
          <p v-if="fieldState.verificationCode.error" class="mollie-field-error">
            {{ fieldState.verificationCode.error }}
          </p>
        </div>
      </div>
      <div class="mollie-field-group">
        <label for="mollie-card-holder" class="mollie-field-label">Name on card</label>
        <div class="mollie-field-wrap" :class="{ 'is-invalid': fieldState.cardHolder.error }">
          <div id="mollie-card-holder" class="mollie-field"></div>
          <span v-show="showPlaceholder('cardHolder')" class="mollie-placeholder">Name on card</span>
        </div>
        <p v-if="fieldState.cardHolder.error" class="mollie-field-error">{{ fieldState.cardHolder.error }}</p>
      </div>
    </div>
    <div v-if="cardErrorMsg" class="text-sm font-medium text-red-600 dark:text-red-400 mt-2 lblError">
      {{ cardErrorMsg }}
    </div>
    <p class="text-xs text-gray-500 dark:text-gray-400 mt-2">
      Pay {{ formattedAmount }} securely by card. Apple Pay may appear during bank verification.
    </p>
  </div>
</template>

<style>
.mollie-payment-element-wrapper {
  position: relative;
  width: 100%;
  margin-top: 1rem;
  margin-bottom: 0.5rem;
}

.mollie-card-panel {
  display: flex;
  flex-direction: column;
  gap: 12px;
}

.mollie-field-label {
  display: block;
  margin-bottom: 3px;
  font-size: 0.875rem;
  line-height: 1.25rem;
  font-weight: 400;
  color: #111827;
}

.mollie-theme-dark .mollie-field-label,
html.dark .mollie-payment-element-wrapper .mollie-field-label {
  color: #d1d5db;
}

.mollie-field-wrap {
  position: relative;
}

.mollie-placeholder {
  position: absolute;
  left: 12px;
  top: 50%;
  transform: translateY(-50%);
  color: #9ca3af;
  font-size: 16px;
  line-height: 1;
  pointer-events: none;
  user-select: none;
}

.mollie-field-error {
  margin: 6px 0 0;
  font-size: 0.875rem;
  line-height: 1.25rem;
  font-weight: 500;
  color: #dc2626;
}

.mollie-theme-dark .mollie-field-error,
html.dark .mollie-payment-element-wrapper .mollie-field-error {
  color: #f87171;
}

.mollie-field-row {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 10px;
}

.mollie-field {
  width: 100%;
  min-height: 44px;
}

.mollie-payment-element-wrapper .mollie-component {
  border-radius: 0.375rem;
  padding: 12px;
  border: 1px solid #d1d5db;
  height: 44px;
  width: 100%;
  background: #ffffff;
  transition: border-color 0.2s ease, box-shadow 0.2s ease, background-color 0.2s ease;
}

.mollie-theme-dark .mollie-component,
html.dark .mollie-payment-element-wrapper .mollie-component {
  background: #374151;
  border-color: #4b5563;
  color: #e5e7eb;
}

.mollie-payment-element-wrapper .mollie-component.has-focus,
.mollie-payment-element-wrapper .mollie-component.is-touched:focus-within {
  border-color: #6366f1;
  box-shadow: 0 0 0 1px rgba(99, 102, 241, 0.5);
}

.mollie-theme-dark .mollie-component.has-focus,
.mollie-theme-dark .mollie-component.is-touched:focus-within,
html.dark .mollie-payment-element-wrapper .mollie-component.has-focus,
html.dark .mollie-payment-element-wrapper .mollie-component.is-touched:focus-within {
  border-color: #818cf8;
  box-shadow: 0 0 0 1px rgba(129, 140, 248, 0.5);
}

.mollie-field-wrap.is-invalid .mollie-component,
.mollie-payment-element-wrapper .mollie-component.is-invalid {
  border-color: #ef4444;
}

.mollie-field-wrap.is-invalid .mollie-component,
.mollie-theme-dark .mollie-component.is-invalid,
html.dark .mollie-payment-element-wrapper .mollie-component.is-invalid,
html.dark .mollie-field-wrap.is-invalid .mollie-component {
  border-color: #f87171;
}

.mollie-payment-element-wrapper .lblError {
  margin: 0;
  min-height: 20px;
}

@media (max-width: 480px) {
  .mollie-field-row {
    grid-template-columns: 1fr;
  }
}
</style>
